/**
* Copyright by: 

* by ESS-Ehrhardt Software Solutions & Service GmbH
* Basselscheider Str. 55
* D-56281 Emmelshausen
* +49 (0) 67 42 / 87 27 0
* info@epg.com
*/
import { TaskAssignmentType } from '../enums/task-assignment-type.enum';
import { TaskStatus } from '../enums/task-status.enum';
import { RecordBaseModel, TracingModel } from './record/record-base.model';
import { TaskReferenceModel } from './task-reference.model';

export class TaskModel {
  constructor(
    public taskId: string,
    public name: string = '',
    public description: string = '',
    public workflowId: string = '',
    public workflow_name?: string,
    public status: TaskStatus = TaskStatus.NEW,
    public tags: string[] = [],
    public references?: TaskReferenceModel[],
    public meta_data?: TaskMetaData,
    public isClaimed: boolean = false,
    public claimedBy: string = '',
    public assignedTo: string[] = [],
    public assignedToEmployeeOrEmployeegroup: TaskAssignmentType = TaskAssignmentType.EMPLOYEE,
    public groupedTask: boolean = false,
    public currentState: number = 0,
    public input: {} = {},
    public output?: TaskDataModel, //= new TaskDataModel(new Map<string, TaskDatumModel[]>()),
    public temp: {} = {},
    public stack: {}[] = [],
    public executedBy: string[] = [],
    public createdAt: string = '',
    public updatedAt: string = '',
    public scheduled_at?: string,
    public scheduled_by?: string,
    public record?: RecordBaseModel[],
    public tracing?: TracingModel,
    public time_tracking?: {
      name: string;
      start: Date;
      employee_id: string;
    }[],
    public incidents?: object[],
    public closedBy?: string,
    public closedAt?: string,
  ) {}
}

export class TaskAttachmentReference {
  constructor(public file_id: string, public file_name: string) {}
  fromJson(json: any): TaskAttachmentReference {
    return new TaskAttachmentReference(json.file_id, json.file_name);
  }
}
export class TaskMetaData {
  constructor(public attachments: TaskAttachmentReference[]) {}

  fromJson(json: any): TaskMetaData {
    return new TaskMetaData(json.attachments);
  }
}

export class TaskDataModel {
  constructor(public nodes: Map<string, TaskDatumModel>) {}

  fromJson(json: Map<string, any>): TaskDataModel {
    let taskDatumNodes: TaskDataModel = new TaskDataModel(new Map<string, TaskDatumModel>());

    Object.entries(json).forEach(([key, value]) => {
      if (value['type'] != null) {
        let taskDatum = Object.create(TaskDatumModel.prototype);
        taskDatumNodes.nodes.set(key, taskDatum.fromJson(value));
      }
    });

    return taskDatumNodes;
  }
}
export class TaskDatumModel {
  constructor(public value: any, public type: TaskDatumType) {}

  fromJson(json: { [key: string]: any }): TaskDatumModel {
    switch (json['type']) {
      case 'ARRAY':
        let taskDataArray = Object.create(TaskDataArray.prototype);
        return taskDataArray.fromJson(json);
      case 'STRING':
        let taskDataString = Object.create(TaskDataString.prototype);
        return taskDataString.fromJson(json);
      case 'TIMESTAMP_ISO':
        let taskTimestamp = Object.create(TaskTimestampISO.prototype);
        return taskTimestamp.fromJson(json);
      case 'BARCODE':
        let taskDataBarcode = Object.create(TaskDataBarcode.prototype);
        return taskDataBarcode.fromJson(json);
      case 'BOOL':
        let taskDataBool = Object.create(TaskDataBool.prototype);
        return taskDataBool.fromJson(json);
      case 'INT':
        let taskDataInt = Object.create(TaskDataInt.prototype);
        return taskDataInt.fromJson(json);
      case 'DOUBLE':
        let taskDataDouble = Object.create(TaskDataDouble.prototype);
        return taskDataDouble.fromJson(json);
      case 'EXTENDED_IMAGE':
        let taskDataImageExtended = Object.create(TaskDataImageExtended.prototype);
        return taskDataImageExtended.fromJson(json);
      case 'IMAGE_JPG':
        let taskDataImageJpeg = Object.create(TaskDataImageJpeg.prototype);
        return taskDataImageJpeg.fromJson(json);
      case 'IMAGE_PNG':
        let taskDataImagePng = Object.create(TaskDataImagePng.prototype);
        return taskDataImagePng.fromJson(json);
      case 'IMAGE_URL':
        let taskDataImageUrl = Object.create(TaskDataImageUrl.prototype);
        return taskDataImageUrl.fromJson(json);
      case 'OBJECT':
        let taskDataObject = Object.create(TaskDataObject.prototype);
        return taskDataObject.fromJson(json);
      case 'SIGNATURE':
        let taskDataSignature = Object.create(TaskDataSignature.prototype);
        return taskDataSignature.fromJson(json);
      default:
        return new TaskDatumModel(`Unknown type: ${json['type']}`, TaskDatumType.UNKNOWN);
    }
  }
}

export enum TaskDatumType {
  STRING,
  INT,
  DOUBLE,
  BOOL,
  ARRAY,
  EXTENDED_IMAGE,
  IMAGE_JPEG,
  IMAGE_PNG,
  IMAGE_URL,
  OBJECT,
  BARCODE,
  SIGNATURE,
  TIMESTAMP_ISO,
  UNKNOWN
}

export class TaskDataString extends TaskDatumModel {
  constructor(public value: string) {
    super(value, TaskDatumType.STRING);
  }

  fromJson(json: { [key: string]: any }): TaskDataString {
    return new TaskDataString(json['value']);
    // return Object.entries(json).forEach(([key, value]) => new TaskDataString(TaskDatumType.STRING, value));
  }
}
export class TaskTimestampISO extends TaskDatumModel {
  constructor(public value: string) {
    super(value, TaskDatumType.TIMESTAMP_ISO);
  }

  fromJson(json: { [key: string]: any }): TaskTimestampISO {
    return new TaskTimestampISO(json['value']);
  }
}

export class TaskDataBool extends TaskDatumModel {
  constructor(public value: boolean) {
    super(value, TaskDatumType.BOOL);
  }

  fromJson(json: { [key: string]: any }): TaskDataBool {
    return new TaskDataBool(json['value'] as boolean);
  }
}

export class TaskDataInt extends TaskDatumModel {
  constructor(public value: number) {
    super(value, TaskDatumType.INT);
  }

  fromJson(json: { [key: string]: any }): TaskDataInt {
    return new TaskDataInt(json['value'] as number);
  }
}

export class TaskDataDouble extends TaskDatumModel {
  constructor(public value: number) {
    super(value, TaskDatumType.DOUBLE);
  }

  fromJson(json: { [key: string]: any }): TaskDataDouble {
    return new TaskDataDouble(json['value'] as number);
  }
}

export class TaskDataArray extends TaskDatumModel {
  constructor(public value: TaskDatumModel[]) {
    super(value, TaskDatumType.ARRAY);
  }

  fromJson(json: { [key: string]: any }): TaskDataArray {
    let taskDatumModel = Object.create(TaskDatumModel.prototype);
    return new TaskDataArray(
      (json['value'] as TaskDatumModel[]).map((e) => taskDatumModel.fromJson(e))
    );
  }
}

export class TaskDataBarcode extends TaskDatumModel {
  constructor(public value: BarcodeData) {
    super(value, TaskDatumType.BARCODE);
  }

  fromJson(json: { [key: string]: any }): TaskDataBarcode {
    let barcodeData = Object.create(BarcodeData.prototype);
    return new TaskDataBarcode(barcodeData.fromJson(json['value']));
  }
}

export class BarcodeData {
  constructor(
    public barcode: TaskDataString,
    public barcodeType: TaskDataString,
    public timestamp: TaskDataString
  ) {}
  fromJson(json: { [key: string]: any }): BarcodeData {
    let taskDataString = Object.create(TaskDataString.prototype);
    return new BarcodeData(
      taskDataString.fromJson(json['barcode']),
      taskDataString.fromJson(json['barcodeType']),
      taskDataString.fromJson(json['timestamp'])
    );
  }
}

export class TaskDataObject extends TaskDatumModel {
  constructor(public value: Map<string, TaskDatumModel>) {
    super(value, TaskDatumType.OBJECT);
  }

  fromJson(json: { [key: string]: any }): TaskDataObject {
    let taskDatumNodes: Map<string, TaskDatumModel> = new Map<string, TaskDatumModel>();
    Object.entries(json['value']).forEach(([key, value]) => {
      // let taskDatums: TaskDatumModel[] = [];

      let taskDatum = Object.create(TaskDatumModel.prototype);
      // taskDatums.push(taskDatum.fromJson(value));

      taskDatumNodes.set(key, taskDatum.fromJson(value));
      // taskDatums = [];
    });
    return new TaskDataObject(taskDatumNodes);
  }
}

export class TaskDataImage extends TaskDatumModel {
  constructor(
    public value: string,
    public type: TaskDatumType,
    public imageRotationDegree?: number
  ) {
    super(value, type);
  }
}

export class TaskDataImageExtended extends TaskDatumModel {
  constructor(public value: ImageExtendedData, public imageRotationDegree?: number) {
    super(value, TaskDatumType.EXTENDED_IMAGE);
  }

  fromJson(json: { [key: string]: any }): TaskDataImageExtended {
    let imageExtendedData = Object.create(ImageExtendedData.prototype);
    return new TaskDataImageExtended(imageExtendedData.fromJson(json['value']));
  }
}

export class ImageExtendedData {
  constructor(
    public image: TaskDataImage,
    public description?: TaskDataString,
    public tags?: TaskDataArray
  ) {}
  fromJson(json: { [key: string]: ImageExtendedData }) {
    let taskDataString = Object.create(TaskDataString.prototype);
    let taskDataImage = Object.create(TaskDataImage.prototype);
    let taskDataArray = Object.create(TaskDataArray.prototype);

    return new ImageExtendedData(
      taskDataImage.fromJson(json['image']),
      json['description'] ? taskDataString.fromJson(json['description']) : null,
      json['tags'] ? taskDataArray.fromJson(json['tags']) : null
    );
  }
}

export class TaskDataImagePng extends TaskDataImage {
  constructor(public value: string, public imageRotationDegree?: number) {
    super(value, TaskDatumType.IMAGE_PNG, imageRotationDegree);
  }

  fromJson(json: { [key: string]: any }): TaskDataImagePng {
    return new TaskDataImagePng(json['value']);
  }
}

export class TaskDataImageJpeg extends TaskDataImage {
  constructor(public value: string, public imageRotationDegree?: number) {
    super(value, TaskDatumType.IMAGE_JPEG, imageRotationDegree);
  }

  fromJson(json: { [key: string]: any }): TaskDataImageJpeg {
    return new TaskDataImageJpeg(json['value']);
  }
}

export class TaskDataImageUrl extends TaskDataImage {
  constructor(public value: string, public imageRotationDegree?: number) {
    super(value, TaskDatumType.IMAGE_URL, imageRotationDegree);
  }

  fromJson(json: { [key: string]: any }): TaskDataImageUrl {
    return new TaskDataImageUrl(json['value']);
  }
}

export class TaskDataSignature extends TaskDatumModel {
  constructor(public value: SignatureData) {
    super(value, TaskDatumType.SIGNATURE);
  }

  fromJson(json: { [key: string]: any }): TaskDataSignature {
    let signatureData = Object.create(SignatureData.prototype);
    return new TaskDataSignature(signatureData.fromJson(json['value']));
  }
}

export class SignatureData {
  constructor(public image: TaskDataImage | TaskDataImageUrl | TaskDataImageExtended) {}
  fromJson(json: { [key: string]: any }): SignatureData {
    let taskDataImage = Object.create(TaskDatumModel.prototype);
    return new SignatureData(taskDataImage.fromJson(json['image']));
  }
}
